import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, X } from 'lucide-react';
import { api } from '../../api/client';

interface WorkstationClient {
  id: string;
  clientName: string;
  machineId: string | null;
  lastSeenAt: string | null;
}

interface WorkstationStatus {
  success: boolean;
  needsPrompt: boolean;
  deviceClientId: string | null;
  userClientId: string | null;
  clients: WorkstationClient[];
}

/**
 * Fragt nach dem Anmelden, an welchem PC der Benutzer sitzt.
 *
 * ⚠️ WARUM GEFRAGT UND NICHT ERKANNT WIRD: Die Weboberfläche kann nicht feststellen, auf welchem
 * Rechner sie läuft — der Browser gibt weder Rechnernamen noch MAC-Adresse preis, und der
 * BISPrint-Client bietet keinen lokalen Zugang an, den man abfragen könnte. Ein Client im selben
 * Raum ist von einem in Thailand nicht zu unterscheiden. Diese Rückfrage IST der Ersatz für die
 * Erkennung, die es nicht gibt.
 *
 * Sie erscheint nur, wenn die Antwort etwas ändert: mehr als ein aktiver Client UND dieser
 * Browser noch keinem zugeordnet (Serverentscheidung, siehe BISPrintService::getWorkstationStatus).
 * Bei einem einzigen Client ist jede Auflösung zwangsläufig richtig — dann bleibt es still.
 */
export function WorkstationPrompt() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<WorkstationStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    api
      .get<WorkstationStatus>('/bisprint/workstation-status')
      .then((res) => {
        if (!cancelled && res?.needsPrompt) setStatus(res);
      })
      // Stiller Fehlschlag ist hier richtig: Die Abfrage läuft direkt nach dem Anmelden und
      // darf niemanden mit einer Fehlermeldung begrüßen, nur weil BISPrint nicht eingerichtet ist.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  const choose = async (clientId: string) => {
    setSaving(true);
    try {
      await api.post('/bisprint/my-workstation', { clientId });
      setDismissed(true);
    } catch (err) {
      console.error('Arbeitsplatz konnte nicht gesetzt werden', err);
      setSaving(false);
    }
  };

  if (!status || dismissed) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl border border-slate-200">
        <div className="flex items-start justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Monitor size={20} className="text-blue-600" />
            <h2 className="font-semibold text-slate-800">
              {t('bisprint.prompt.title', 'An welchem PC arbeiten Sie?')}
            </h2>
          </div>
          {/* „Später" muss möglich sein — sonst blockiert eine Druckerfrage die ganze Anwendung. */}
          <button
            onClick={() => setDismissed(true)}
            className="text-slate-400 hover:text-slate-600"
            aria-label={t('common.close', 'Schließen')}
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-slate-600 mb-4">
            {t(
              'bisprint.prompt.description',
              'Damit Etiketten und Picklisten an dem Drucker herauskommen, an dem Sie stehen. Sie können das jederzeit in den Druck-Einstellungen ändern.'
            )}
          </p>

          <div className="space-y-2">
            {status.clients.map((c) => (
              <button
                key={c.id}
                disabled={saving}
                onClick={() => choose(c.id)}
                className="w-full flex items-center gap-3 p-3 border border-slate-200 rounded-lg text-left hover:border-blue-400 hover:bg-blue-50 disabled:opacity-50"
              >
                <Monitor size={18} className="text-slate-500 shrink-0" />
                <span className="font-medium text-slate-800">{c.clientName}</span>
                {c.id === status.userClientId && (
                  <span className="ml-auto text-xs text-slate-500">
                    {t('bisprint.prompt.lastUsed', 'zuletzt genutzt')}
                  </span>
                )}
              </button>
            ))}
          </div>

          <button
            onClick={() => setDismissed(true)}
            className="mt-4 text-xs text-slate-500 hover:text-slate-700"
          >
            {t('bisprint.prompt.later', 'Später entscheiden')}
          </button>
        </div>
      </div>
    </div>
  );
}
